/**
 * The search box's draft text vs `?q=` in the URL (issue #333, prod regression
 * after v1.8.0).
 *
 * Binding the input straight to the URL drops keystrokes: `setSearchParams`
 * lands asynchronously, so a fast keystroke is applied to a stale value (typing
 * «garcía» left `?q=a`). The typed text therefore lives in local state — it
 * drives the instant filtering — and reaches the URL debounced. The URL flows
 * back into the draft only when nothing typed is still pending, i.e. on an
 * outside change (back from the detail, reload, a shared link).
 */

import { useCallback, useEffect, useRef, useState } from "react";

export type SearchDraft = {
  /** What the input shows and the table filters by. */
  text: string;
  /** Typed text not yet confirmed by the URL; `null` when settled. */
  pending: string | null;
};

export function typeDraft(_draft: SearchDraft, text: string): SearchDraft {
  return { text, pending: text };
}

export function reconcileFromUrl(draft: SearchDraft, urlQ: string): SearchDraft {
  if (draft.pending !== null) {
    // Stale echoes while typing are ignored; the URL catching up settles it.
    return urlQ === draft.pending ? { text: draft.pending, pending: null } : draft;
  }
  return urlQ === draft.text ? draft : { text: urlQ, pending: null };
}

export const SEARCH_URL_DEBOUNCE_MS = 250;

/** React binding: returns the draft text and a setter; `commit` writes `?q=`. */
export function useSearchDraft(
  urlQ: string,
  commit: (q: string) => void,
): [string, (text: string) => void] {
  const [draft, setDraft] = useState<SearchDraft>({ text: urlQ, pending: null });
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const commitRef = useRef(commit);
  commitRef.current = commit;

  useEffect(() => {
    setDraft((current) => reconcileFromUrl(current, urlQ));
  }, [urlQ]);

  useEffect(() => () => clearTimeout(timer.current), []);

  const setText = useCallback((text: string) => {
    setDraft((current) => typeDraft(current, text));
    clearTimeout(timer.current);
    timer.current = setTimeout(() => commitRef.current(text), SEARCH_URL_DEBOUNCE_MS);
  }, []);

  return [draft.text, setText];
}
